import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import { emitActivityChanged } from '@utils/activityEvents';
import PageHeader from '@components/PageHeader';
import PageSpinner from '@components/PageSpinner';

// The guest scanned the QR on a fridge's screen. "Connect" links their stay to it; from then on
// whatever they take out is added to their cart (and put back, taken off) — shown live here and on
// the fridge. They confirm here or on the fridge, and it's charged to their room.

interface Product { menuItemId: number; name: string; price: number; imageUrl?: string }
interface Line extends Product { quantity: number; lineTotal: number }
interface Question { eventId: string; index: number; direction: string; quantity: number; note?: string; candidates: Product[] }
interface Cart {
  claimId: string;
  state: string;
  lines: Line[];
  questions: Question[];
  total: number;
  currencySymbol: string;
  orderNumber?: string;
  chargedAmount?: number;
  error?: string;
}
interface ClaimInfo { deviceName?: string; stationName?: string; state: string; linked: boolean; cart?: Cart }

const card: React.CSSProperties = { background: '#fff', borderRadius: 16, padding: '1.25rem', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', marginBottom: '1rem' };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  width: '100%', padding: '0.9rem', borderRadius: 12, border: 'none', fontWeight: 700, fontSize: '1rem', background: bg, color, cursor: 'pointer', marginTop: 8,
});

export default function DeviceClaimPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslationStore();
  const [info, setInfo] = useState<ClaimInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tr = (key: string, fallback: string) => { const v = t(key); return v && v !== key ? v : fallback; };
  const hotelSlug = localStorage.getItem('gcxp_hotel_slug');
  const base = `/api/v1/${hotelSlug}/device-claims/${token}`;

  const errorOf = (err: unknown, fallback: string) => {
    const e = err as { response?: { data?: { error?: string; code?: string } } };
    return { message: e.response?.data?.error || fallback, code: e.response?.data?.code };
  };

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<ClaimInfo>(base);
      setInfo(res.data);
    } catch (err) {
      setError(errorOf(err, tr('device_claim.invalid', 'This code isn’t valid any more. Scan the fridge screen again.')).message);
    }
  }, [base]);

  useEffect(() => { if (token && hotelSlug) load(); }, [token, hotelSlug, load]);

  // Live while linked — the fridge adds items as they go out
  const live = info?.linked && (info.cart?.state === 'active' || info.cart?.state === 'charging');
  useEffect(() => {
    if (!live) return;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [live, load]);

  const act = async (path: string, body: object = {}) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post<Cart>(`${base}/${path}`, body);
      setInfo(prev => ({ ...(prev ?? { state: res.data.state }), linked: true, state: res.data.state, cart: res.data }));
      if (res.data.state === 'charged') emitActivityChanged();
    } catch (err) {
      const { message, code } = errorOf(err, tr('device_claim.failed', 'Something went wrong. Please try again.'));
      if (code === 'verify') {
        // Charges need this phone verified first — then connect again.
        promptVerifyToOrder(() => act(path, body));
        setError(tr('device_claim.verify', 'Please verify your phone number to charge your room.'));
      } else {
        setError(message);
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!info && !error) return <PageSpinner />;

  const cart = info?.cart;
  const sym = cart?.currencySymbol ?? '';
  const title = info?.deviceName || info?.stationName || tr('device_claim.fridge', 'Fridge');

  return (
    <div style={{ padding: '1rem' }}>
      <PageHeader title={<>🧊 {title}</>} />

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 12, padding: '1rem', marginBottom: '1rem' }}>
          ⚠️ {error}
        </div>
      )}

      {info && !info.linked && info.state === 'pending' && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>{tr('device_claim.connectTitle', 'Connect to this fridge')}</h3>
          <p style={{ color: '#4B5563' }}>
            {tr('device_claim.connectBody', 'Take what you like: everything you take out is added to your cart, and putting it back takes it off. Confirm when you’re done — it’s charged to your room.')}
          </p>
          <button style={btn('#16A34A')} disabled={busy} onClick={() => act('connect')}>
            🔗 {tr('device_claim.connect', 'Connect')}
          </button>
        </div>
      )}

      {info && !info.linked && info.state !== 'pending' && !error && (
        <div style={card}>{tr('device_claim.expired', 'This code isn’t open any more. Scan the fridge screen again.')}</div>
      )}

      {cart && info?.linked && cart.state === 'active' && (
        <>
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>🛒 {tr('device_claim.yourCart', 'Your cart')}</h3>
            {cart.lines.length === 0 && cart.questions.length === 0 && (
              <p style={{ color: '#6B7280' }}>{tr('device_claim.empty', 'Take an item out of the fridge — it shows up here.')}</p>
            )}
            {cart.lines.map(l => (
              <div key={l.menuItemId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.4rem 0' }}>
                {l.imageUrl && <img src={l.imageUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8 }} />}
                <span style={{ flex: 1 }}>{l.quantity}× {l.name}</span>
                <span>{l.lineTotal > 0 ? `${sym}${l.lineTotal.toFixed(2)}` : '—'}</span>
              </div>
            ))}
            {cart.lines.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, borderTop: '1px solid #E5E7EB', marginTop: 8, paddingTop: 8 }}>
                <span>{tr('device_claim.total', 'Total')}</span>
                <span>{sym}{cart.total.toFixed(2)}</span>
              </div>
            )}
          </div>

          {cart.questions.map(q => (
            <div key={`${q.eventId}-${q.index}`} style={card}>
              <h4 style={{ marginTop: 0 }}>
                ❓ {q.direction === 'in' ? tr('device_claim.whichBack', 'Which one did you put back?') : tr('device_claim.whichOut', 'Which one did you take?')}
                {q.quantity > 1 ? ` (${q.quantity}×)` : ''}
              </h4>
              {q.note && <p style={{ color: '#6B7280', marginTop: 0 }}>{q.note}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {q.candidates.map(c => (
                  <button key={c.menuItemId} disabled={busy}
                    style={{ ...btn('#F3F4F6', '#111827'), marginTop: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
                    onClick={() => act('answers', { eventId: q.eventId, index: q.index, menuItemId: c.menuItemId })}>
                    {c.imageUrl && <img src={c.imageUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />}
                    <span>{c.name}</span>
                    {c.price > 0 && <small>{sym}{c.price.toFixed(2)}</small>}
                  </button>
                ))}
                <button disabled={busy} style={{ ...btn('#F3F4F6', '#6B7280'), marginTop: 0 }}
                  onClick={() => act('answers', { eventId: q.eventId, index: q.index, menuItemId: null })}>
                  {tr('device_claim.noneOfThese', 'None of these')}
                </button>
              </div>
            </div>
          ))}

          {cart.lines.length > 0 && (
            <button style={btn('#16A34A')} disabled={busy || cart.questions.length > 0} onClick={() => act('confirm')}>
              {cart.questions.length > 0
                ? tr('device_claim.answerFirst', 'Answer the question above first')
                : `✅ ${tr('device_claim.confirm', 'Confirm and charge my room')} · ${sym}${cart.total.toFixed(2)}`}
            </button>
          )}
          {cart.lines.length === 0 && cart.questions.length === 0 && (
            <button style={btn('transparent', '#6B7280')} disabled={busy} onClick={() => act('disconnect')}>
              {tr('device_claim.disconnect', 'Disconnect')}
            </button>
          )}
        </>
      )}

      {cart && info?.linked && cart.state === 'charging' && <PageSpinner />}

      {cart && info?.linked && (cart.state === 'charged' || cart.state === 'ended' || cart.state === 'failed') && (
        <div style={card}>
          <div style={{ textAlign: 'center', fontSize: '2.5rem' }}>{cart.state === 'failed' ? '⚠️' : '✅'}</div>
          <h3 style={{ textAlign: 'center', margin: '0.25rem 0 1rem' }}>
            {cart.state === 'charged'
              ? `${tr('device_claim.charged', 'Charged to your room')}${cart.orderNumber ? ` · #${cart.orderNumber}` : ''}`
              : cart.state === 'failed'
                ? (cart.error ?? tr('device_claim.chargeFailed', 'The charge didn’t go through — reception will sort it out.'))
                : tr('device_claim.ended', 'Disconnected — nothing was charged.')}
          </h3>
          {cart.state === 'charged' && cart.chargedAmount != null && (
            <div style={{ textAlign: 'center', fontWeight: 800 }}>{sym}{cart.chargedAmount.toFixed(2)}</div>
          )}
        </div>
      )}
    </div>
  );
}
